/**
 * CMR Read Service - Fetches patient CMR (Continuous Medical Record) data for ChartMind
 * 
 * This service enables ChartMind to pull patient health data when a patient
 * is linked via QR code scanning. It reads from the patient's Firestore
 * healthRecords document and medications subcollection.
 * 
 * Data structure returned:
 * {
 *   demographics: { firstName, lastName, dateOfBirth, sex, phone, ... },
 *   pmh: [{ condition, diagnosedYear, status, notes }, ...],
 *   psh: [{ procedure, year, hospital, surgeon, notes }, ...],
 *   medications: [{ name, dosage, frequency, ... }, ...],
 *   allergies: [{ allergen, type, reaction }, ...],
 *   other: { socialHistory, familyHistory, smokingStatus, ... }
 * }
 */

import { db } from '@healthdesk/shared-services';

/**
 * Fetch complete CMR data for a patient
 * @param {string} patientId - The patient's Firebase UID
 * @returns {Promise<Object>} Complete CMR data or null if not found
 */
export async function fetchPatientCMR(patientId) {
  if (!patientId) {
    console.warn('[CMR Read] No patient ID provided');
    return null;
  }
  
  try {
    
    // Fetch user document with healthRecords
    const userDoc = await db.collection('users').doc(patientId).get();
    
    if (!userDoc.exists) {
      console.warn('[CMR Read] Patient document not found:', patientId);
      return null;
    }
    
    const userData = userDoc.data();
    const healthRecords = userData?.healthRecords || {};
    
    
    // Fetch medications from subcollection
    const medsSnapshot = await db.collection('users').doc(patientId)
      .collection('medications').get();
    const medications = medsSnapshot.docs.map(doc => {
      const data = doc.data();
      // Normalize medication name field (support both 'name' and 'medicationName')
      // Also normalize field names: patient saves 'prescribedBy', card expects 'prescriber'
      return {
        id: doc.id,
        ...data,
        name: data.name || data.medicationName || '', // Ensure 'name' field exists
        prescriber: data.prescriber || data.prescribedBy || '', // Normalize prescriber field
        reason: data.reason || data.notes || '', // Some cards might use 'reason' for notes
      };
    });
    
    
    // Build CMR data structure
    // Handle dateOfBirth - could be ISO string, Timestamp, or Date
    let dateOfBirth = null;
    if (healthRecords.dateOfBirth) {
      if (healthRecords.dateOfBirth.toDate) {
        // Firestore Timestamp
        dateOfBirth = healthRecords.dateOfBirth.toDate().toISOString();
      } else if (healthRecords.dateOfBirth instanceof Date) {
        // JavaScript Date
        dateOfBirth = healthRecords.dateOfBirth.toISOString();
      } else if (typeof healthRecords.dateOfBirth === 'string') {
        // Already ISO string
        dateOfBirth = healthRecords.dateOfBirth;
      }
    }

    // Build CMR data structure - ALWAYS return data even if empty arrays/objects
    // CRITICAL: Read data directly from healthRecords - don't use fallbacks that might hide missing data
    
    // Past Medical History - read directly, ensure it's an array
    let pmhArray = [];
    if (healthRecords.pmh) {
      if (Array.isArray(healthRecords.pmh)) {
        pmhArray = healthRecords.pmh.filter(p => p && p.condition); // Filter out invalid entries
      } else if (typeof healthRecords.pmh === 'object' && healthRecords.pmh.condition) {
        pmhArray = [healthRecords.pmh];
      }
    }
    
    // Past Surgical History - read directly, ensure it's an array
    let pshArray = [];
    if (healthRecords.psh) {
      if (Array.isArray(healthRecords.psh)) {
        pshArray = healthRecords.psh.filter(s => s && s.procedure);
      } else if (typeof healthRecords.psh === 'object' && healthRecords.psh.procedure) {
        pshArray = [healthRecords.psh];
      }
    }
    
    // Allergies - read directly, ensure it's an array
    let allergiesArray = [];
    if (healthRecords.allergies) {
      if (Array.isArray(healthRecords.allergies)) {
        allergiesArray = healthRecords.allergies.filter(a => a && a.allergen);
      } else if (typeof healthRecords.allergies === 'object' && healthRecords.allergies.allergen) {
        allergiesArray = [healthRecords.allergies];
      }
    }
    
    const cmrData = {
      // Demographics - read directly from healthRecords (where patient saves it)
      demographics: {
        firstName: healthRecords.firstName || userData?.name?.split(' ')[0] || '',
        lastName: healthRecords.lastName || userData?.name?.split(' ').slice(1).join(' ') || '',
        dateOfBirth: dateOfBirth,
        sex: healthRecords.sex || '',
        phone: healthRecords.phone || userData?.phone || '',
        email: healthRecords.email || userData?.email || '',
        address: healthRecords.address || null,
        emergencyContact: healthRecords.emergencyContact || null,
        weight: healthRecords.weight != null ? healthRecords.weight : null,
        weightDate: healthRecords.weightDate || null,
      },
      
      // Past Medical History - use the processed array
      pmh: pmhArray,
      
      // Past Surgical History - use the processed array
      psh: pshArray,
      
      // Medications (from subcollection) - always an array
      medications: Array.isArray(medications) ? medications : [],
      
      // Allergies - use the processed array
      allergies: allergiesArray,
      
      // Other (social/family history) - always an object
      other: (healthRecords.other && typeof healthRecords.other === 'object') ? healthRecords.other : {},
      
      // Metadata
      updatedAt: healthRecords.updatedAt || null,
      patientId: patientId,
    };
    
    // CRITICAL: Ensure we ALWAYS return a valid structure, even if all data is empty
    // This prevents the cards from thinking no data was fetched
    
    if (cmrData.pmh.length > 0) {
    } else {
    }
    if (cmrData.psh.length > 0) {
    } else {
    }
    if (cmrData.medications.length > 0) {
    } else {
    }
    if (cmrData.allergies.length > 0) {
    } else {
    }
    
    // CRITICAL: ALWAYS return a valid structure, even if all data is empty
    // This ensures cmrCards is never null, only the arrays/objects inside are empty
    return cmrData;
    
  } catch (error) {
    console.error('[CMR Read] Error fetching CMR:', error);
    // Even on error, return a valid empty structure so cmrCards isn't null
    return {
      demographics: {
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        sex: '',
        phone: '',
        email: '',
        address: null,
        emergencyContact: null,
        weight: null,
        weightDate: null,
      },
      pmh: [],
      psh: [],
      medications: [],
      allergies: [],
      other: {},
      updatedAt: null,
      patientId: patientId,
    };
  }
}

/**
 * Fetch specific section of CMR data
 * @param {string} patientId - The patient's Firebase UID
 * @param {string} section - Section name: 'demographics', 'pmh', 'psh', 'medications', 'allergies', 'other'
 * @returns {Promise<Object|Array>} Section data or null
 */
export async function fetchPatientCMRSection(patientId, section) {
  if (!patientId || !section) {
    return null;
  }
  
  try {
    // Special handling for medications (subcollection)
    if (section === 'medications') {
      const medsSnapshot = await db.collection('users').doc(patientId)
        .collection('medications').get();
      return medsSnapshot.docs.map(doc => {
        const data = doc.data();
        // Normalize medication name field (support both 'name' and 'medicationName')
        return {
          id: doc.id,
          ...data,
          name: data.name || data.medicationName || '', // Ensure 'name' field exists
        };
      });
    }
    
    // All other sections from healthRecords
    const userDoc = await db.collection('users').doc(patientId).get();
    if (!userDoc.exists) return null;
    
    const userData = userDoc.data();
    const healthRecords = userData?.healthRecords || {};
    
    switch (section) {
      case 'demographics':
        return {
          firstName: healthRecords.firstName || userData?.name?.split(' ')[0] || '',
          lastName: healthRecords.lastName || userData?.name?.split(' ').slice(1).join(' ') || '',
          dateOfBirth: healthRecords.dateOfBirth || null,
          sex: healthRecords.sex || '',
          phone: healthRecords.phone || userData?.phone || '',
          email: healthRecords.email || userData?.email || '',
          address: healthRecords.address || null,
          emergencyContact: healthRecords.emergencyContact || null,
          weight: healthRecords.weight != null ? healthRecords.weight : null,
          weightDate: healthRecords.weightDate || null,
        };
      case 'pmh':
        return healthRecords.pmh || [];
      case 'psh':
        return healthRecords.psh || [];
      case 'allergies':
        return healthRecords.allergies || [];
      case 'other':
        return healthRecords.other || {};
      default:
        console.warn('[CMR Read] Unknown section:', section);
        return null;
    }
  } catch (error) {
    console.error('[CMR Read] Error fetching CMR section:', error);
    return null;
  }
}

/**
 * Calculate age from date of birth
 * @param {string|Date} dateOfBirth - Date of birth
 * @returns {number|null} Age in years or null
 */
export function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;
  
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * Format CMR data for LLM context (used in chart generation prompts)
 * @param {Object} cmrData - Complete CMR data object
 * @returns {string} Formatted string for LLM context
 */
export function formatCMRForLLM(cmrData) {
  if (!cmrData) return '';
  
  const sections = [];
  
  // Demographics
  const demo = cmrData.demographics;
  if (demo?.firstName || demo?.lastName) {
    const age = calculateAge(demo.dateOfBirth);
    const ageStr = age ? `, ${age}yo` : '';
    const sexStr = demo.sex ? ` ${demo.sex}` : '';
    sections.push(`PATIENT: ${demo.firstName} ${demo.lastName}${ageStr}${sexStr}`);
  }
  
  // PMH
  if (cmrData.pmh?.length > 0) {
    const conditions = cmrData.pmh
      .map(c => `${c.condition}${c.status === 'active' ? ' (active)' : c.status === 'resolved' ? ' (resolved)' : ''}`)
      .join(', ');
    sections.push(`PMH: ${conditions}`);
  } else {
    sections.push('PMH: None documented');
  }
  
  // PSH
  if (cmrData.psh?.length > 0) {
    const surgeries = cmrData.psh
      .map(s => `${s.procedure}${s.year ? ` (${s.year})` : ''}`)
      .join(', ');
    sections.push(`PSH: ${surgeries}`);
  } else {
    sections.push('PSH: None documented');
  }
  
  // Medications
  if (cmrData.medications?.length > 0) {
    const meds = cmrData.medications
      .map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}`)
      .join(', ');
    sections.push(`MEDICATIONS: ${meds}`);
  } else {
    sections.push('MEDICATIONS: None documented');
  }
  
  // Allergies
  if (cmrData.allergies?.length > 0) {
    const allergies = cmrData.allergies
      .map(a => `${a.allergen}${a.reaction ? ` - ${a.reaction}` : ''}`)
      .join(', ');
    sections.push(`ALLERGIES: ${allergies}`);
  } else {
    sections.push('ALLERGIES: NKDA');
  }
  
  // Social/Family History
  const other = cmrData.other;
  if (other) {
    if (other.smokingStatus) sections.push(`SMOKING: ${other.smokingStatus}`);
    if (other.alcoholUse) sections.push(`ALCOHOL: ${other.alcoholUse}`);
    if (other.familyHistory) sections.push(`FAMILY HX: ${other.familyHistory}`);
  }

  // Review of Systems (from SecureLink intake extraction)
  const ros = cmrData.ros ?? cmrData.cmrCards?.ros;
  if (Array.isArray(ros) && ros.length > 0) {
    const rosLines = ros
      .map((item) => {
        const system = item.system ?? item.systemName ?? '';
        const value = (item.value ?? item.summary ?? '').toString().trim() || 'No complaints';
        return system ? `${system}: ${value}` : value;
      })
      .filter(Boolean);
    if (rosLines.length > 0) {
      sections.push('REVIEW OF SYSTEMS (ROS):\n' + rosLines.join('\n'));
    }
  }
  
  return sections.join('\n');
}

/**
 * Format CMR data for LLM context with filtering based on card inclusions
 * Only includes sections where the toggle is checked (true)
 * Labels each section clearly as patient-provided data via SecureLink
 * 
 * @param {Object} cmrData - Complete CMR data object
 * @param {Object} inclusions - Object specifying which sections to include
 *   { demographics: true, pmh: true, psh: true, medications: true, allergies: true, other: true }
 * @returns {string} Formatted string for LLM context with only included sections
 */
export function formatCMRForLLMFiltered(cmrData, inclusions = {}) {
  if (!cmrData) return '';
  
  // Default all to true if not specified (backwards compatible)
  const include = {
    demographics: inclusions.demographics !== false,
    pmh: inclusions.pmh !== false,
    psh: inclusions.psh !== false,
    medications: inclusions.medications !== false,
    allergies: inclusions.allergies !== false,
    other: inclusions.other !== false,
  };
  
  const sections = [];
  
  // Demographics
  if (include.demographics) {
    const demo = cmrData.demographics;
    if (demo?.firstName || demo?.lastName) {
      const age = calculateAge(demo.dateOfBirth);
      const ageStr = age ? `, ${age}yo` : '';
      const sexStr = demo.sex ? ` ${demo.sex}` : '';
      sections.push(`PATIENT DEMOGRAPHICS (via SecureLink): ${demo.firstName} ${demo.lastName}${ageStr}${sexStr}`);
    }
  }
  
  // PMH
  if (include.pmh) {
    if (cmrData.pmh?.length > 0) {
      const conditions = cmrData.pmh
        .map(c => `${c.condition}${c.status === 'active' ? ' (active)' : c.status === 'resolved' ? ' (resolved)' : ''}`)
        .join(', ');
      sections.push(`PATIENT PAST MEDICAL HISTORY (via SecureLink): ${conditions}`);
    } else {
      sections.push('PATIENT PAST MEDICAL HISTORY (via SecureLink): None documented');
    }
  }
  
  // PSH
  if (include.psh) {
    if (cmrData.psh?.length > 0) {
      const surgeries = cmrData.psh
        .map(s => `${s.procedure}${s.year ? ` (${s.year})` : ''}`)
        .join(', ');
      sections.push(`PATIENT PAST SURGICAL HISTORY (via SecureLink): ${surgeries}`);
    } else {
      sections.push('PATIENT PAST SURGICAL HISTORY (via SecureLink): None documented');
    }
  }
  
  // Medications - CRITICAL for medication inference
  if (include.medications) {
    if (cmrData.medications?.length > 0) {
      const meds = cmrData.medications
        .map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}${m.frequency ? ` ${m.frequency}` : ''}`)
        .join(', ');
      sections.push(`PATIENT MEDICATIONS (via SecureLink): ${meds}`);
      sections.push('NOTE: Use the medication list above to INFER underlying conditions (e.g., antihypertensives → hypertension)');
    } else {
      sections.push('PATIENT MEDICATIONS (via SecureLink): None documented');
    }
  }
  
  // Allergies
  if (include.allergies) {
    if (cmrData.allergies?.length > 0) {
      const allergies = cmrData.allergies
        .map(a => `${a.allergen}${a.reaction ? ` - ${a.reaction}` : ''}`)
        .join(', ');
      sections.push(`PATIENT ALLERGIES (via SecureLink): ${allergies}`);
    } else {
      sections.push('PATIENT ALLERGIES (via SecureLink): NKDA');
    }
  }
  
  // Social/Family History
  if (include.other) {
    const other = cmrData.other;
    if (other) {
      const otherItems = [];
      if (other.smokingStatus) otherItems.push(`Smoking: ${other.smokingStatus}`);
      if (other.alcoholUse) otherItems.push(`Alcohol: ${other.alcoholUse}`);
      if (other.drugUse) otherItems.push(`Drug use: ${other.drugUse}`);
      if (other.familyHistory) otherItems.push(`Family Hx: ${other.familyHistory}`);
      if (other.socialHistory) otherItems.push(`Social Hx: ${other.socialHistory}`);
      
      if (otherItems.length > 0) {
        sections.push(`PATIENT SOCIAL/FAMILY HISTORY (via SecureLink): ${otherItems.join('; ')}`);
      }
    }
  }
  
  // Conversation Results (from SecureLink intake conversation)
  if (cmrData.conversationResults?.findings?.length > 0) {
    const cr = cmrData.conversationResults;
    let convoSection = '\n--- INTAKE CONVERSATION RESULTS ---';
    if (cr.title) convoSection += `\nConversation: ${cr.title}`;
    if (cr.summary) convoSection += `\nSummary: ${cr.summary}`;
    convoSection += '\nFindings:';
    cr.findings.forEach(f => {
      convoSection += `\n  - ${f.label}: ${f.value}`;
    });
    sections.push(convoSection);
  }

  // Review of Systems (from SecureLink intake extraction)
  const includeRos = inclusions.ros !== false;
  const ros = cmrData.ros ?? cmrData.cmrCards?.ros;
  if (includeRos && Array.isArray(ros) && ros.length > 0) {
    const rosLines = ros
      .map((item) => {
        const system = item.system ?? item.systemName ?? '';
        const value = (item.value ?? item.summary ?? '').toString().trim() || 'No complaints';
        return system ? `${system}: ${value}` : value;
      })
      .filter(Boolean);
    if (rosLines.length > 0) {
      sections.push('\n--- REVIEW OF SYSTEMS (ROS) ---\n' + rosLines.join('\n'));
    }
  }

  // Log what was included/excluded for debugging
  
  return sections.join('\n');
}

/**
 * Check if CMR data has meaningful content
 * @param {Object} cmrData - Complete CMR data object
 * @returns {boolean} True if CMR has any meaningful data
 */
export function hasCMRData(cmrData) {
  if (!cmrData) return false;
  
  return (
    cmrData.demographics?.firstName ||
    cmrData.pmh?.length > 0 ||
    cmrData.psh?.length > 0 ||
    cmrData.medications?.length > 0 ||
    cmrData.allergies?.length > 0 ||
    Object.keys(cmrData.other || {}).length > 0
  );
}

export default {
  fetchPatientCMR,
  fetchPatientCMRSection,
  calculateAge,
  formatCMRForLLM,
  formatCMRForLLMFiltered,
  hasCMRData,
};

